import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { get, pickBy, map, values } from 'lodash'

import { YES_NO_OPTIONS } from 'utils/enums/questions'
import { MONTH_LIST } from 'utils/enums/months'
import { validateRequiredField } from 'utils/helpers/validators/byType/requiredField'
import { validateYear } from 'utils/helpers/validators/byType/yearField'
import { validateNumberInRange } from 'utils/helpers/validators/byType/number'
import { FormRadioButtonGroup } from 'components/form/radioButtonGroup/FormRadioButtonGroup'
import { UploadScan } from 'components/uploadScan/UploadScan'
import { FormInput } from 'components/form/input/FormInput'
import { FormDropdown } from 'components/form/dropdown/FormDropdown'
import { Col, Row } from 'components/grid'
import { Text } from 'components/text/Text'

import styles from './CAForm.module.scss'

export const CaFormConfig = {
  wasSubjectOfAbuse: {
    required: formData => !formData.unknownPerson.value,
    validator: validateRequiredField,
  },
  wasSubjectOfAbuseYear: {
    isTrimPayloadValue: true,
    required: formData => !!formData.wasSubjectOfAbuse.value,
    validator: validateYear,
  },
  wasSubjectOfAbuseMonth: {
    validator: validateRequiredField,
  },
  wasSubjectOfAbuseDay: {
    isTrimPayloadValue: true,
    validator: validateNumberInRange,
    min: 1,
    max: 31,
  },
  wasSubjectOfAbuseScan1: {
    required: formData => !!formData.wasSubjectOfAbuse.value,
    validator: validateRequiredField,
  },
  wasSubjectOfAbuseScan2: {},
  wasSubjectOfAbuseScan3: {},

  wasResigned: {
    required: formData => !formData.unknownPerson.value,
    validator: validateRequiredField,
  },

  wasLicenseRevoked: {
    required: formData => !formData.unknownPerson.value,
    validator: validateRequiredField,
  },
  wasLicenseRevokedYear: {
    isTrimPayloadValue: true,
    required: formData => !!formData.wasLicenseRevoked.value,
    validator: validateYear,
  },
  wasLicenseRevokedMonth: {
    validator: validateRequiredField,
  },
  wasLicenseRevokedDay: {
    isTrimPayloadValue: true,
    validator: validateNumberInRange,
    min: 1,
    max: 31,
  },
  wasLicenseRevokedScan1: {
    required: formData => !!formData.wasLicenseRevoked.value,
    validator: validateRequiredField,
  },
  wasLicenseRevokedScan2: {},
  wasLicenseRevokedScan3: {},
}

export const CAForm = ({ formData, handleFieldChange, handleUploadScan, handleDeleteScan, setIsFormBusy, formId }) => {
  const wasLicenseRevoked = get(formData, 'wasLicenseRevoked.value')
  const wasSubjectOfAbuse = get(formData, 'wasSubjectOfAbuse.value')
  const MAX_SCANS_COUNT = 3
  const wasSubjectOfAbuseScans = map(
    pickBy(formData, field => field.name.startsWith('wasSubjectOfAbuseScan') && field.value),
    field => field.value,
  )

  const wasLicenseRevokedScans = map(
    pickBy(formData, field => field.name.startsWith('wasLicenseRevokedScan') && field.value),
    field => field.value,
  )

  return (
    <div className={styles.form}>
      <FormRadioButtonGroup
        title="Was this employee the subject of a substantiated report of child abuse or sexual conduct related to the applicant's employment with the education provider?"
        className={wasSubjectOfAbuse ? styles.questionSmallBottomGap : styles.radioButtonQuestion}
        options={YES_NO_OPTIONS}
        field={formData.wasSubjectOfAbuse}
        handleFieldChange={handleFieldChange}
      />

      {wasSubjectOfAbuse && (
        <div className={styles.panel}>
          <Row className={styles.questionSmallBottomGap}>
            <Col>
              <Text weight="bold">Dates of Any Substantiated Reports</Text>
            </Col>
          </Row>

          <Row>
            <Col className={styles.dateGroup}>
              <FormInput
                className={classNames(styles.dateInputBlock, styles.dateInputYear)}
                title="Year"
                placeholder="yyyy"
                maxLength={4}
                field={formData.wasSubjectOfAbuseYear}
                onChange={handleFieldChange('wasSubjectOfAbuseYear')}
                validationPattern={value => !isNaN(value) || value === ''}
              />
              <FormDropdown
                className={classNames(styles.dateInputBlock, styles.monthDropdown)}
                rowWidth={175}
                title="Month"
                placeholder="Month"
                items={values(MONTH_LIST)}
                field={formData.wasSubjectOfAbuseMonth}
                onChange={handleFieldChange('wasSubjectOfAbuseMonth')}
              />
              <FormInput
                className={classNames(styles.dateInputBlock, styles.dateInput)}
                title="Day"
                placeholder="dd"
                maxLength={2}
                field={formData.wasSubjectOfAbuseDay}
                onChange={handleFieldChange('wasSubjectOfAbuseDay')}
                validationPattern={value => !isNaN(value) || value === ''}
              />
            </Col>
          </Row>

          <Row topIndent>
            <Col>
              <UploadScan
                title="Please attach below the definitions of child abuse and sexual conduct used by the District when the
                education provider determined that any reports were substantiated and the standards used by the District
                to determine whether any reports were substantiated"
                showHint
                formId={formId}
                files={wasSubjectOfAbuseScans}
                onIsBusyChanged={setIsFormBusy}
                onUpload={scan => handleUploadScan('wasSubjectOfAbuseScan', wasSubjectOfAbuseScans.length, scan)}
                onRemove={index => handleDeleteScan('wasSubjectOfAbuseScan', wasSubjectOfAbuseScans.length, index)}
                disabled={wasSubjectOfAbuseScans.length === MAX_SCANS_COUNT}
              />
            </Col>
          </Row>
        </div>
      )}

      <FormRadioButtonGroup
        title="Is this employee the subject of an ongoing investigation related to a report of suspected child abuse or sexual conduct?"
        className={styles.radioButtonQuestion}
        options={YES_NO_OPTIONS}
        field={formData.wasResigned}
        handleFieldChange={handleFieldChange}
      />

      <FormRadioButtonGroup
        title="Was this employee convicted of a crime listed in California Assembly Bill (AB2534) while employed in this district?"
        className={wasLicenseRevoked ? styles.questionSmallBottomGap : styles.radioButtonQuestion}
        options={YES_NO_OPTIONS}
        field={formData.wasLicenseRevoked}
        handleFieldChange={handleFieldChange}
      />

      {wasLicenseRevoked && (
        <div className={styles.panel}>
          <Row className={styles.questionSmallBottomGap}>
            <Col>
              <Text weight="bold">Dates of Conviction</Text>
            </Col>
          </Row>

          <Row>
            <Col className={styles.dateGroup}>
              <FormInput
                className={classNames(styles.dateInputBlock, styles.dateInputYear)}
                title="Year"
                placeholder="yyyy"
                maxLength={4}
                field={formData.wasLicenseRevokedYear}
                onChange={handleFieldChange('wasLicenseRevokedYear')}
                validationPattern={value => !isNaN(value) || value === ''}
              />
              <FormDropdown
                className={classNames(styles.dateInputBlock, styles.monthDropdown)}
                rowWidth={175}
                title="Month"
                placeholder="Month"
                items={values(MONTH_LIST)}
                field={formData.wasLicenseRevokedMonth}
                onChange={handleFieldChange('wasLicenseRevokedMonth')}
              />
              <FormInput
                className={classNames(styles.dateInputBlock, styles.dateInput)}
                title="Day"
                placeholder="dd"
                maxLength={2}
                field={formData.wasLicenseRevokedDay}
                onChange={handleFieldChange('wasLicenseRevokedDay')}
                validationPattern={value => !isNaN(value) || value === ''}
              />
            </Col>
          </Row>

          <Row topIndent>
            <Col>
              <UploadScan
                title="Please enclose below copies of the discipline records"
                showHint
                formId={formId}
                files={wasLicenseRevokedScans}
                onIsBusyChanged={setIsFormBusy}
                onUpload={scan => handleUploadScan('wasLicenseRevokedScan', wasLicenseRevokedScans.length, scan)}
                onRemove={index => handleDeleteScan('wasLicenseRevokedScan', wasLicenseRevokedScans.length, index)}
                disabled={wasLicenseRevokedScans.length === MAX_SCANS_COUNT}
              />
            </Col>
          </Row>
        </div>
      )}
    </div>
  )
}

CAForm.propTypes = {
  formData: PropTypes.object.isRequired,
  formMeta: PropTypes.object.isRequired,
  handleFieldChange: PropTypes.func.isRequired,
  handleUploadScan: PropTypes.func.isRequired,
  handleDeleteScan: PropTypes.func.isRequired,
  setIsFormBusy: PropTypes.func.isRequired,
  formId: PropTypes.string.isRequired,
}
